import { FC } from "react"
import { ConfigAdminToggleRow } from "../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../config-admin-panel"
import { RateCardsConfigDetailsData } from "../../../../../../types"
import TermLoanRow from "./TermLoanRow"
import { toDefaultTabOptions } from "./utils"

interface LoanTypePanelProps {
  group: "purchase" | "refinance"
  label: string
  loanTypes: { key: string; label: string }[]
  details: RateCardsConfigDetailsData
  setDetails: (details: RateCardsConfigDetailsData) => void
}

const LoanTypePanel: FC<LoanTypePanelProps> = ({
  group,
  label,
  loanTypes,
  details,
  setDetails
}) => {
  const tabs = details.tabs?.[group] ?? []
  const enabled = tabs.length > 0

  const getTabOption = (loanType: string) =>
    tabs.find(option => option.type === loanType)

  const handleGroupToggle = (value: boolean) => {
    if (!value) {
      setDetails({ ...details, tabs: { ...details.tabs, [group]: [] } })
      return
    }
    if (details.tabs[group].length === 0) {
      setDetails({
        ...details,
        tabs: { ...details.tabs, [group]: toDefaultTabOptions(loanTypes) }
      })
    }
  }

  const handleLoanToggle = (
    loanType: string,
    defaultLabel: string,
    nextEnabled: boolean
  ) => {
    const currentOptions = [...details.tabs[group]]
    const index = currentOptions.findIndex(option => option.type === loanType)
    if (nextEnabled && index === -1) {
      currentOptions.push({ type: loanType, label: defaultLabel })
    } else if (!nextEnabled && index > -1) {
      currentOptions.splice(index, 1)
    }
    setDetails({
      ...details,
      tabs: { ...details.tabs, [group]: currentOptions }
    })
  }

  const handleLabelChange = (
    loanType: string,
    defaultLabel: string,
    nextLabel: string
  ) => {
    const currentOptions = [...details.tabs[group]]
    const index = currentOptions.findIndex(option => option.type === loanType)
    if (index === -1) return
    currentOptions[index] = {
      ...currentOptions[index],
      label: nextLabel.trim() || defaultLabel
    }
    setDetails({
      ...details,
      tabs: { ...details.tabs, [group]: currentOptions }
    })
  }

  if (loanTypes.length === 0) {
    return (
      <ConfigAdminPanel>
        <div className="px-4 py-4">
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <p className="mt-1 text-sm text-gray-400">
            No {label.toLowerCase()} scenarios configured. Open Settings to add{" "}
            {label.toLowerCase()} scenario preferences.
          </p>
        </div>
      </ConfigAdminPanel>
    )
  }

  return (
    <ConfigAdminPanel>
      <ConfigAdminToggleRow
        label={label}
        description={`Control which ${label.toLowerCase()} loan types appear on the rate cards`}
        enabled={enabled}
        onChange={handleGroupToggle}
        last={false}
      />
      {enabled && (
        <div className="divide-y divide-gray-100 border-t border-gray-100">
          {loanTypes.map(lt => (
            <TermLoanRow
              key={lt.key}
              loanKey={lt.key}
              defaultLabel={lt.label}
              labelValue={getTabOption(lt.key)?.label ?? lt.label}
              enabled={Boolean(getTabOption(lt.key))}
              onLabelChange={v => handleLabelChange(lt.key, lt.label, v)}
              onEnabledChange={v => handleLoanToggle(lt.key, lt.label, v)}
            />
          ))}
        </div>
      )}
    </ConfigAdminPanel>
  )
}

export default LoanTypePanel
